import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Download, Share2, Leaf, ShoppingCart, ChefHat, User, AlertTriangle, Check, X, ArrowRight, Clock, Users, Heart, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { useCart } from "../context/CartContext";
import { trackRecommendationViewed, trackRecommendationHerbClick, trackRecommendationStoreClick } from "../lib/analytics";
import { BodyDiagram } from "../components/BodyDiagram";
import { ShareScorecard } from "../components/ShareScorecard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Recommendations = () => {
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [data, setData] = useState(null);
  const [wlProducts, setWlProducts] = useState(null);
  const [herbs, setHerbs] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [bodyAreas, setBodyAreas] = useState([]);
  const [safetyWarnings, setSafetyWarnings] = useState("");
  const [lifestyleTips, setLifestyleTips] = useState([]);
  const [loading, setLoading] = useState(true);

  const parseRecommendations = useCallback(async (text) => {
    try {
      // Parse herbs from HERB_LIST_START to HERB_LIST_END
      const herbListMatch = text.match(/HERB_LIST_START([\s\S]*?)HERB_LIST_END/);
      if (herbListMatch) {
        const herbLines = herbListMatch[1].trim().split('\n').filter(l => l.trim());
        const parsedHerbs = [];
        
        for (const line of herbLines) {
          const parts = line.split('|');
          if (parts.length >= 7) {
            const herbName = parts[0].replace(/^\s*\d+\.\s*/, '').trim();
            parsedHerbs.push({
              name: herbName,
              scientificName: parts[1].trim(),
              bodyArea: parts[2].trim(),
              benefit: parts[3].trim(),
              howToUse: parts[4].trim(),
              dosage: parts[5].trim(),
              safety: parts[6].trim()
            });
          }
        }
        
        // Fetch herb images from database
        const response = await axios.get(`${API}/herbs`);
        const allHerbs = response.data;
        
        const enrichedHerbs = parsedHerbs.map(herb => {
          // Try multiple matching strategies
          const herbNameLower = herb.name.toLowerCase().trim();
          
          // 1. Exact match
          let match = allHerbs.find(h => h.name.toLowerCase() === herbNameLower);
          
          // 2. Partial match - herb name contains database name
          if (!match) {
            match = allHerbs.find(h => herbNameLower.includes(h.name.toLowerCase()));
          }
          
          // 3. Database name contains herb name
          if (!match) {
            match = allHerbs.find(h => h.name.toLowerCase().includes(herbNameLower));
          }
          
          // 4. Try without special chars (e.g., "DGL" -> "Licorice")
          if (!match && herbNameLower.includes('licorice')) {
            match = allHerbs.find(h => h.name.toLowerCase().includes('licorice'));
          }
          if (!match && herbNameLower.includes('marshmallow')) {
            match = allHerbs.find(h => h.name.toLowerCase().includes('marshmallow'));
          }
          if (!match && herbNameLower.includes('slippery elm')) {
            match = allHerbs.find(h => h.name.toLowerCase().includes('slippery elm'));
          }
          if (!match && herbNameLower.includes('ginger')) {
            match = allHerbs.find(h => h.name.toLowerCase().includes('ginger'));
          }
          if (!match && herbNameLower.includes('astragalus')) {
            match = allHerbs.find(h => h.name.toLowerCase().includes('astragalus'));
          }
          
          return {
            ...herb,
            image: match?.image_url || 'https://images.unsplash.com/photo-1603887918482-c144a3c20a06?w=800&q=85',
            id: match?.id,
            price_gbp: match?.price_gbp
          };
        });
        
        setHerbs(enrichedHerbs);
      }
      
      // Parse recipes with proper extraction
      const recipeMatch = text.match(/RECIPE_START([\s\S]*?)RECIPE_END/);
      if (recipeMatch) {
        const recipeText = recipeMatch[1].trim();
        const recipeBlocks = recipeText.split(/Recipe \d+:/).filter(r => r.trim());
        
        const parsedRecipes = recipeBlocks.map(block => {
          // Extract recipe name (first line)
          const lines = block.split('\n').filter(l => l.trim());
          const name = lines[0]?.trim() || '';
          
          // Extract ingredients (everything after "Ingredients:" until "Instructions:")
          const ingredientsStart = block.indexOf('Ingredients:');
          const instructionsStart = block.indexOf('Instructions:');
          const ingredientsText = ingredientsStart !== -1 && instructionsStart !== -1
            ? block.substring(ingredientsStart + 'Ingredients:'.length, instructionsStart).trim()
            : '';
          
          // Extract instructions (everything after "Instructions:" until "Benefits:")
          const benefitsStart = block.indexOf('Benefits:');
          const instructionsText = instructionsStart !== -1 && benefitsStart !== -1
            ? block.substring(instructionsStart + 'Instructions:'.length, benefitsStart).trim()
            : '';
          
          // Extract benefits (everything after "Benefits:")
          const benefitsText = benefitsStart !== -1
            ? block.substring(benefitsStart + 'Benefits:'.length).trim()
            : '';
          
          return {
            name: name,
            ingredients: ingredientsText,
            instructions: instructionsText,
            benefits: benefitsText
          };
        });
        setRecipes(parsedRecipes);
      }
      
      // Parse body areas
      const bodyAreasMatch = text.match(/BODY_AREAS:\s*(.+)/);
      if (bodyAreasMatch) {
        const areas = bodyAreasMatch[1].toLowerCase().split(',').map(a => a.trim());
        setBodyAreas(areas);
      }
      
      // Parse safety warnings
      const safetyMatch = text.match(/SAFETY_WARNINGS:\s*([\s\S]*?)(?=LIFESTYLE_TIPS:|$)/);
      if (safetyMatch) {
        setSafetyWarnings(safetyMatch[1].trim());
      }
      
      // Parse lifestyle tips
      const lifestyleMatch = text.match(/LIFESTYLE_TIPS:\s*([\s\S]*?)(?=IMPORTANT|$)/);
      if (lifestyleMatch) {
        const tips = lifestyleMatch[1].trim().split('\n').filter(t => t.trim()).map(t => t.replace(/^[-•]\s*/, ''));
        setLifestyleTips(tips);
      }
      
    } catch (error) {
      console.error('Error parsing recommendations:', error);
      // Fallback: try to extract any herb names from the text
      const herbNames = ['Chamomile', 'Lavender', 'Ginger', 'Turmeric', 'Peppermint'];
      const response = await axios.get(`${API}/herbs`);
      const allHerbs = response.data;
      
      const fallbackHerbs = herbNames.map(name => {
        const match = allHerbs.find(h => h.name.toLowerCase() === name.toLowerCase());
        return match ? {
          name: match.name,
          scientificName: match.scientific_name,
          bodyArea: 'Multiple systems',
          benefit: match.benefits[0] || 'General wellness',
          howToUse: match.uses[0] || 'Tea',
          dosage: 'As directed',
          safety: match.precautions,
          image: match.image_url,
          id: match.id
        } : null;
      }).filter(Boolean);
      
      setHerbs(fallbackHerbs);
    }
    
    setLoading(false);
  }, []);

  useEffect(() => {
    const storedData = sessionStorage.getItem('recommendations');
    if (storedData) {
      const recData = JSON.parse(storedData);
      setData(recData);
      trackRecommendationViewed();
      parseRecommendations(recData.recommendations);
      return;
    }
    const rid = new URLSearchParams(window.location.search).get('rid');
    if (rid) {
      axios.get(`${API}/recommendations/${rid}`)
        .then((res) => {
          sessionStorage.setItem('recommendations', JSON.stringify(res.data));
          setData(res.data);
          trackRecommendationViewed();
          parseRecommendations(res.data.recommendations);
        })
        .catch(() => {
          toast.error("We couldn't find that recommendation. Please retake the assessment.");
          navigate('/questionnaire');
        });
      return;
    }
    toast.error("No recommendations found. Please complete the questionnaire first.");
    navigate('/questionnaire');
  }, [navigate, parseRecommendations]);

  const handleBuyAll = () => {
    trackRecommendationStoreClick();
    const purchasable = herbs.filter(h => h.id);
    if (purchasable.length === 0) {
      navigate("/store");
      return;
    }
    purchasable.forEach(h => addItem(h.id));
    toast.success(`${purchasable.length} recommended herbs added to your cart!`);
    navigate("/cart");
  };

  const handleDownload = () => {
    if (!data) return;
    
    let content = `ELITE HERBALIST - YOUR PERSONALIZED WELLNESS PLAN\n\n`;
    content += `Wellness Goals: ${(data?.wellness_goals || []).join(', ')}\n`;
    content += `Age: ${data.age} | Gender: ${data.gender}\n\n`;
    content += `=== YOUR RECOMMENDED HERBS ===\n\n`;
    
    herbs.forEach((herb, i) => {
      content += `${i + 1}. ${herb.name} (${herb.scientificName})\n`;
      content += `   Benefits: ${herb.benefit}\n`;
      content += `   How to Use: ${herb.howToUse}\n`;
      content += `   Dosage: ${herb.dosage}\n`;
      content += `   Safety: ${herb.safety}\n\n`;
    });
    
    if (recipes.length > 0) {
      content += `\n=== HERBAL RECIPES ===\n\n`;
      recipes.forEach((recipe, i) => {
        content += `${i + 1}. ${recipe.name}\n`;
        content += `   Ingredients: ${recipe.ingredients}\n`;
        content += `   Instructions: ${recipe.instructions}\n\n`;
      });
    }
    
    content += `\n=== IMPORTANT SAFETY INFORMATION ===\n${safetyWarnings}\n\n`;
    content += `Generated: ${new Date(data.timestamp).toLocaleDateString()}\n`;
    content += `\nDISCLAIMER: For educational purposes only. Consult healthcare provider before use.`;
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'elite-herbalist-wellness-plan.txt';
    link.click();
    URL.revokeObjectURL(url);
    toast.success("Wellness plan downloaded!");
  };

  let wlBanner = null;
  try { wlBanner = JSON.parse(sessionStorage.getItem('wl_brand_banner') || 'null'); } catch { wlBanner = null; }

  useEffect(() => {
    if (!wlBanner?.key || !herbs.length) return;
    const names = herbs.map((h) => h.name && h.name.trim().toLowerCase()).filter(Boolean).join(",");
    if (!names) return;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/wl/embed/${wlBanner.key}/products`, { params: { herbs: names } })
      .then(({ data: d }) => setWlProducts(d.matches || {}))
      .catch(() => {});
  }, [herbs]); // eslint-disable-line

  if (loading || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-emerald-50 to-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Generating your personalized plan...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white">
      {wlBanner?.store_name && (
        <div className="px-4 py-2.5 text-center text-sm font-semibold text-white" style={{ backgroundColor: wlBanner.primary_colour || "#065f46" }} data-testid="rec-wl-banner">
          Your assessment from <b>{wlBanner.store_name}</b> <span className="opacity-75 font-normal">· powered by Elite Herbalist</span>
        </div>
      )}
      {/* Hero Section */}
      <div className="bg-primary text-white py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
              <Leaf className="w-8 h-8" />
            </div>
          </div>
          <h1 className="font-playfair text-4xl md:text-5xl font-bold text-center mb-3">
            Your Personalized Wellness Plan
          </h1>
          <p className="text-center text-white/90 text-lg">
            {herbs.length} herbs selected for: <span className="font-semibold">{(data?.wellness_goals || []).join(', ')}</span>
          </p>
          
          <div className="flex flex-wrap justify-center gap-3 mt-6">
            <Button
              onClick={handleBuyAll}
              data-testid="rec-buy-all-btn"
              className="bg-accent hover:bg-accent/90 text-white rounded-full shadow-lg"
              size="lg"
            >
              <ShoppingCart className="mr-2 w-5 h-5" />
              Buy All Herbs
            </Button>
            <Button
              onClick={handleDownload}
              variant="outline"
              className="bg-white/10 border-white/30 text-white hover:bg-white/20 rounded-full backdrop-blur-sm"
            >
              <Download className="mr-2 w-4 h-4" />
              Download Plan
            </Button>
            <ShareScorecard goals={data.wellness_goals} herbs={herbs} />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Body Diagram + Quick Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {/* Body Diagram */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
            <h3 className="font-playfair text-xl font-bold text-gray-900 mb-4 text-center">
              Areas of Effect
            </h3>
            <BodyDiagram affectedAreas={bodyAreas} gender={data?.gender} />
          </div>

          {/* Complete User Profile */}
          <div className="md:col-span-2 space-y-4">
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
              <h3 className="font-playfair text-xl font-bold text-gray-900 mb-4 flex items-center">
                <User className="w-6 h-6 text-primary mr-2" />
                Your Complete Profile
              </h3>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="flex items-start">
                  <User className="w-5 h-5 text-primary mr-2 mt-1" />
                  <div>
                    <p className="text-sm text-gray-500">Age</p>
                    <p className="font-semibold text-gray-900">{data.age} years old</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <User className="w-5 h-5 text-primary mr-2 mt-1" />
                  <div>
                    <p className="text-sm text-gray-500">Gender</p>
                    <p className="font-semibold text-gray-900">{data.gender}</p>
                  </div>
                </div>
              </div>
              
              <div className="border-t border-gray-200 pt-4 space-y-3">
                <div className="flex items-start">
                  <Leaf className="w-5 h-5 text-primary mr-2 mt-1 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm text-gray-500 mb-1">Wellness Goals</p>
                    <div className="flex flex-wrap gap-2">
                      {data.wellness_goals.map((goal) => (
                        <span key={goal} className="inline-block px-3 py-1 bg-emerald-100 text-emerald-800 rounded-full text-xs font-medium">
                          {goal}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                
                {data.current_ailments && (
                  <div className="flex items-start bg-blue-50 rounded-lg p-3">
                    <AlertTriangle className="w-5 h-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-blue-900 mb-1">Medical Conditions</p>
                      <p className="text-sm text-blue-800">{data.current_ailments}</p>
                    </div>
                  </div>
                )}
                
                {data.current_medications && (
                  <div className="flex items-start bg-purple-50 rounded-lg p-3">
                    <AlertTriangle className="w-5 h-5 text-purple-600 mr-2 mt-1 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-purple-900 mb-1">Current Medications</p>
                      <p className="text-sm text-purple-800">{data.current_medications}</p>
                    </div>
                  </div>
                )}
                
                {data.gender === 'Female' && (data.is_pregnant || data.is_trying_to_conceive) && (
                  <div className="flex items-start bg-pink-50 rounded-lg p-3 border border-pink-200">
                    <AlertTriangle className="w-5 h-5 text-pink-600 mr-2 mt-1 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-pink-900 mb-1">Women's Health</p>
                      {data.is_pregnant && (
                        <p className="text-sm text-pink-800 font-semibold">⚠️ Currently Pregnant - Extra safety precautions applied</p>
                      )}\n                      {data.is_trying_to_conceive && !data.is_pregnant && (
                        <p className="text-sm text-pink-800">Trying to conceive - Fertility-safe recommendations</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {safetyWarnings && (
              <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-6">
                <div className="flex items-start">
                  <AlertTriangle className="w-6 h-6 text-amber-600 mr-3 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-playfair text-lg font-bold text-amber-900 mb-2">
                      Important Safety Information
                    </h3>
                    <p className="text-amber-800 text-sm leading-relaxed whitespace-pre-line">
                      {safetyWarnings}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Herb Cards - CLEAR LIST */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-playfair text-3xl font-bold text-gray-900">
              Your Recommended Herbs ({herbs.length})
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {herbs.map((herb, index) => (
              <div
                key={herb.name || index}
                className="bg-white rounded-xl shadow-lg border-2 border-gray-200 overflow-hidden hover:shadow-2xl hover:border-primary transition-all duration-300"
              >
                {/* Herb Image */}
                <div className="relative h-40 overflow-hidden">
                  <img
                    src={herb.image}
                    alt={herb.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-3 left-3 bg-primary text-white px-3 py-1 rounded-full text-sm font-bold">
                    #{index + 1}
                  </div>
                </div>

                {/* Herb Details */}
                <div className="p-5 space-y-3">
                  <div>
                    <h3 className="font-playfair text-2xl font-bold text-gray-900">
                      {herb.name}
                    </h3>
                    <p className="font-mono text-xs text-gray-500 italic">
                      {herb.scientificName}
                    </p>
                  </div>

                  <div className="bg-emerald-50 rounded-lg p-3">
                    <p className="text-sm font-semibold text-primary mb-1">Targets:</p>
                    <p className="text-sm text-gray-700">{herb.bodyArea}</p>
                  </div>

                  {wlBanner && wlProducts?.[herb.name?.trim().toLowerCase()] && (
                    <div className="rounded-lg p-3 border-2" style={{ borderColor: wlBanner.primary_colour || "#047857", backgroundColor: "#f6faf7" }} data-testid={`wl-product-match-${index}`}>
                      <p className="text-[10px] font-bold uppercase tracking-wide mb-1.5" style={{ color: wlBanner.primary_colour || "#047857" }}>
                        Available at {wlBanner.store_name}
                      </p>
                      {wlProducts[herb.name.trim().toLowerCase()].map((p) => (
                        <p key={p.sku} className="text-xs text-gray-700 flex justify-between gap-2">
                          <span className="truncate">{p.product_name}{p.product_form ? ` · ${p.product_form}` : ""}</span>
                          {p.price != null && <span className="font-semibold shrink-0">£{Number(p.price).toFixed(2)}</span>}
                        </p>
                      ))}
                      <p className="text-[10px] text-gray-400 mt-1.5">Ask in store or at the till — mention your assessment.</p>
                    </div>
                  )}

                  <div>
                    <p className="text-sm font-semibold text-gray-900 mb-1">Benefits:</p>
                    <p className="text-sm text-gray-700">{herb.benefit}</p>
                  </div>

                  <div>
                    <p className="text-sm font-semibold text-gray-900 mb-1">How to Use:</p>
                    <p className="text-sm text-gray-700">{herb.howToUse}</p>
                  </div>

                  <div>
                    <p className="text-sm font-semibold text-gray-900 mb-1">Dosage:</p>
                    <p className="text-sm text-gray-700">{herb.dosage}</p>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-xs font-semibold text-amber-900 mb-1">Safety:</p>
                    <p className="text-xs text-amber-800">{herb.safety}</p>
                  </div>

                  <div className="flex gap-2">
                    {herb.id && (
                      <Button
                        onClick={() => { addItem(herb.id); toast.success(`${herb.name} added to cart`); }}
                        className="flex-1 rounded-full bg-accent hover:bg-accent/90"
                        size="sm"
                        data-testid={`rec-add-cart-${herb.id}`}
                      >
                        <ShoppingCart className="mr-2 w-4 h-4" />
                        Add to Cart{herb.price_gbp ? ` · £${herb.price_gbp.toFixed(2)}` : ""}
                      </Button>
                    )}
                    {herb.id && (
                      <Link to={`/herbs/${herb.id}`} onClick={() => trackRecommendationHerbClick(herb)}>
                        <Button variant="outline" className="rounded-full" size="sm">
                          View Details
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Transition to shop */}
          <div className="mt-10 bg-primary rounded-3xl p-7 sm:p-9 text-center" data-testid="rec-shop-transition">
            <h3 className="font-playfair text-2xl sm:text-3xl font-bold text-white mb-2">Ready to start your plan?</h3>
            <p className="text-white/85 text-sm sm:text-base mb-6 max-w-xl mx-auto">
              Add all {herbs.length} recommended herbs to your basket in one click — real botanicals, typically shipped in 3–7 working days.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button onClick={handleBuyAll} data-testid="rec-buy-all-bottom-btn"
                      className="w-full sm:w-auto h-13 px-8 py-3 rounded-full bg-white text-primary hover:bg-white/90 font-bold text-base shadow-xl">
                <ShoppingCart className="mr-2 w-5 h-5" /> Add All {herbs.length} Herbs to Basket
              </Button>
              <Link to="/store" data-testid="rec-browse-store-link"
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border-2 border-white/40 text-white hover:bg-white/10 font-semibold rounded-full px-7 py-3 text-sm transition-colors">
                Browse the full Herb Store
              </Link>
            </div>
          </div>
        </div>

        {/* Herbal Preparation Recipes Section - Full Details */}
        {recipes.length > 0 && (
          <div className="mb-12">
            <div className="flex items-center mb-6">
              <ChefHat className="w-8 h-8 text-primary mr-3" />
              <h2 className="font-playfair text-3xl font-bold text-gray-900">
                Herbal Preparation Recipes
              </h2>
            </div>
            <p className="text-gray-600 mb-8">Complete step-by-step instructions for preparing your recommended herbs</p>

            {/* Check if carbonated mode and show Infuso equipment guide */}
            {sessionStorage.getItem('recommendations') && 
             JSON.parse(sessionStorage.getItem('recommendations')).recommendations?.toLowerCase().includes('infuso') && (
              <div className="mb-8 bg-gradient-to-r from-primary/10 to-emerald-500/10 border-2 border-primary/30 rounded-2xl p-6">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0">
                    <img 
                      src="https://static.prod-images.emergentagent.com/jobs/3c7d4d91-69a4-4dbc-840a-7249f347f396/images/a93ab32367234ddd2bfdcce6b2f8536bb368d5de13a5b3a83d267fcdc25eb403.jpeg" 
                      alt="Infuso Soda Machine" 
                      className="w-32 h-32 object-contain rounded-lg bg-white p-2"
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-playfair text-xl font-bold text-gray-900 mb-2 flex items-center">
                      <Sparkles className="w-5 h-5 text-primary mr-2" />
                      Infuso Carbonation Equipment
                    </h3>
                    <p className="text-gray-700 text-sm mb-3 leading-relaxed">
                      These recipes use the <strong>Infuso Soda Water Machine</strong> with 640g CO₂ or N₂O cylinders for carbonation and infusion.
                    </p>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div className="flex items-start gap-2">
                        <img 
                          src="https://static.prod-images.emergentagent.com/jobs/3c7d4d91-69a4-4dbc-840a-7249f347f396/images/d1eb9d287f9f5568618dee7d3de541661b3daa2aed181157c1dd2b8508fe0d7d.jpeg" 
                          alt="CO2 Cylinder" 
                          className="w-12 h-12 object-contain bg-white rounded p-1"
                        />
                        <div>
                          <p className="text-xs font-semibold text-gray-900">640g CO₂ Cylinder</p>
                          <p className="text-xs text-gray-600">For carbonation</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <img 
                          src="https://static.prod-images.emergentagent.com/jobs/3c7d4d91-69a4-4dbc-840a-7249f347f396/images/d1eb9d287f9f5568618dee7d3de541661b3daa2aed181157c1dd2b8508fe0d7d.jpeg" 
                          alt="N2O Cylinder" 
                          className="w-12 h-12 object-contain bg-white rounded p-1"
                        />
                        <div>
                          <p className="text-xs font-semibold text-gray-900">640g N₂O Cylinder</p>
                          <p className="text-xs text-gray-600">For infusion</p>
                        </div>
                      </div>
                    </div>
                    <a 
                      href="https://infuso.com/shop/all/infuso-n2o-640g-single-cylinder/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-primary hover:text-emerald-700 font-semibold text-sm mt-3 transition-colors"
                    >
                      View Infuso Equipment →
                    </a>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-8">
              {recipes.map((recipe, index) => {
                // Detect if recipe is carbonated/sparkling
                const isCarbonated = recipe.name.toLowerCase().includes('sparkling') || 
                                    recipe.name.toLowerCase().includes('fizz') || 
                                    recipe.name.toLowerCase().includes('carbonated') ||
                                    recipe.name.toLowerCase().includes('soda') ||
                                    recipe.instructions.toLowerCase().includes('infuso') ||
                                    recipe.instructions.toLowerCase().includes('carbonate');
                
                // Assign images based on recipe type
                const getRecipeImage = (recipeName) => {
                  if (isCarbonated) {
                    // Use Infuso machine image for carbonated recipes
                    return 'https://static.prod-images.emergentagent.com/jobs/3c7d4d91-69a4-4dbc-840a-7249f347f396/images/a93ab32367234ddd2bfdcce6b2f8536bb368d5de13a5b3a83d267fcdc25eb403.jpeg';
                  }
                  const name = recipeName.toLowerCase();
                  if (name.includes('tea') || name.includes('infusion')) {
                    return 'https://images.unsplash.com/photo-1762328842522-cf9aeae3f73c?w=400&q=85';
                  } else if (name.includes('tincture') || name.includes('extract')) {
                    return 'https://images.unsplash.com/photo-1722931303388-527993417e23?w=400&q=85';
                  } else if (name.includes('decoction') || name.includes('brew')) {
                    return 'https://images.unsplash.com/photo-1621781999729-93d2b682ef87?w=400&q=85';
                  } else {
                    return 'https://images.unsplash.com/photo-1762328842522-cf9aeae3f73c?w=400&q=85';
                  }
                };

                // Parse instructions into steps
                const instructionSteps = recipe.instructions.split(/\d+\.\s+/).filter(s => s.trim());

                return (
                  <div 
                    key={recipe.name || `recipe-${index}`}
                    className="bg-white rounded-2xl shadow-xl border-2 border-gray-100 overflow-hidden"
                  >
                    <div className="grid md:grid-cols-5 gap-6">
                      {/* Recipe Image */}
                      <div className="md:col-span-2 relative h-64 md:h-auto overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100">
                        <img
                          src={getRecipeImage(recipe.name)}
                          alt={recipe.name}
                          className={`w-full h-full ${isCarbonated ? 'object-contain p-8' : 'object-cover'}`}
                        />
                        <div className="absolute top-4 left-4 bg-gradient-to-r from-primary to-emerald-600 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                          Recipe #{index + 1}
                        </div>
                        {isCarbonated && (
                          <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs font-bold text-primary border-2 border-primary/30 shadow-lg flex items-center gap-1">
                            <Sparkles className="w-3 h-3" />
                            Infuso Method
                          </div>
                        )}
                      </div>

                      {/* Recipe Content */}
                      <div className="md:col-span-3 p-6 space-y-5">
                        {/* Title */}
                        <div>
                          <h3 className="font-playfair text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                            {recipe.name}
                          </h3>
                          <div className="flex items-center gap-4 text-sm text-gray-600">
                            <div className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              <span>10-15 min</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Users className="w-4 h-4" />
                              <span>1-2 servings</span>
                            </div>
                          </div>
                        </div>

                        {/* Health Benefits - Full Display */}
                        <div className="bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl p-4 border-l-4 border-primary">
                          <div className="flex items-start gap-2">
                            <Heart className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="font-semibold text-primary mb-1">Health Benefits:</p>
                              <p className="text-sm text-gray-700 leading-relaxed">{recipe.benefits}</p>
                            </div>
                          </div>
                        </div>

                        {/* Ingredients - Full List */}
                        <div>
                          <p className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                            <Leaf className="w-4 h-4 text-primary" />
                            Ingredients:
                          </p>
                          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                            <p className="text-sm text-gray-700 leading-relaxed">{recipe.ingredients}</p>
                          </div>
                        </div>

                        {/* Preparation Instructions - Full Steps */}
                        <div>
                          <p className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                            <ChefHat className="w-4 h-4 text-primary" />
                            Preparation Steps:
                          </p>
                          <div className="space-y-3">
                            {instructionSteps.length > 1 ? (
                              instructionSteps.map((step, stepIndex) => (
                                <div key={`${step.slice(0, 24)}-${stepIndex}`} className="flex items-start gap-3 bg-gray-50 rounded-lg p-3 border border-gray-200">
                                  <div className="w-7 h-7 bg-gradient-to-br from-primary to-emerald-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">
                                    {stepIndex + 1}
                                  </div>
                                  <p className="text-sm text-gray-700 leading-relaxed pt-1">{step}</p>
                                </div>
                              ))
                            ) : (
                              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                <p className="text-sm text-gray-700 leading-relaxed">{recipe.instructions}</p>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Link to detailed page */}
                        <Link 
                          to={`/recipe/${index}`}
                          className="inline-flex items-center text-primary hover:text-emerald-700 font-semibold text-sm transition-colors"
                        >
                          <span>View detailed recipe page</span>
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Lifestyle Tips */}
        {lifestyleTips.length > 0 && (
          <div className="mb-12">
            <h2 className="font-playfair text-3xl font-bold text-gray-900 mb-6">
              Lifestyle Recommendations
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {lifestyleTips.map((tip, index) => (
                <div key={tip || index} className="bg-white rounded-lg border border-gray-200 p-4 flex items-start">
                  <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5 mr-3" />
                  <p className="text-gray-700">{tip}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Final CTA removed — rec-shop-transition band above covers the shop handoff */}

        {/* Disclaimer */}
        <div className="mt-8 bg-gray-50 border border-gray-200 rounded-lg p-6">
          <p className="text-sm text-gray-600 text-center">
            <strong>Medical Disclaimer:</strong> This information is for educational purposes only. 
            Always consult your healthcare provider before starting any herbal regimen.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Recommendations;